/**
 * install-prompt.mjs — hold the browser's install offer, so the app can make it at the right moment.
 *
 * WHY THIS IS NOT INSIDE THE DIALOG. `beforeinstallprompt` fires once, seconds after load, and only
 * if the browser has decided the app qualifies. A listener registered when a dialog opens has already
 * missed it, and the dialog would then report "your browser does not offer installation" about a
 * browser that offered it and was not listening. So the event is caught at startup — imported from
 * main.jsx before anything renders — and held here.
 *
 * WHAT THIS MODULE REFUSES TO DO is guess. There are three genuinely different states and they are
 * kept apart, because collapsing them produces a button that does nothing:
 *   - already installed        → do not offer; say it is installed
 *   - offer held               → a real button that installs
 *   - no offer                 → the browser (iOS Safari, Firefox) has no install API at all, so the
 *                                honest answer is the manual gesture, never a dead button
 */
let deferred = null;
const subs = new Set();
const fire = () => subs.forEach((f) => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();          // hold it; the browser's own banner is not where this belongs
    deferred = e;
    fire();
  });
  /* Installed elsewhere in this session — drop the offer, or the button outlives what it does. */
  window.addEventListener('appinstalled', () => { deferred = null; fire(); });
}

export const canInstall = () => deferred !== null;

/* Standalone display-mode is how a PWA reports it is running as an installed app; navigator.standalone
   is the older iOS spelling of the same fact. Either one means: do not offer to install this. */
export const isInstalled = () => typeof window !== 'undefined'
  && (window.matchMedia?.('(display-mode: standalone)')?.matches === true
      || window.navigator?.standalone === true);

export const onInstallChange = (f) => { subs.add(f); return () => subs.delete(f); };

export async function promptInstall() {
  if (!deferred) return 'unavailable';
  const e = deferred;
  deferred = null;                              // a captured prompt is single-use; a retry throws
  fire();
  e.prompt();
  const { outcome } = await e.userChoice;       // 'accepted' | 'dismissed'
  return outcome;
}
